import React, { FC, useState, useCallback, useMemo } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import { BlockHeader } from "@app/components/common/block-header/BlockHeader";
import { Tabs } from "@app/components/common/tabs/Tabs";
import { IPlanGroup } from "@app/models/storyblok/blocks/plans";
import { IRichText, TextType } from "@app/models/storyblok/basic";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { Plan } from "./components/plan/Plan";

type Props = {
  title: IRichText<TextType>;
  planGroups: IPlanGroup[];
};

export const Plans: FC<Props> = ({ title, planGroups }) => {
  const [selectedGroup, setSelectedGroup] = useState(planGroups[0]._uid);

  const destructuredPlans = useMemo(
    () =>
      planGroups.reduce((plans, group) => {
        return [
          ...plans,
          ...group.plans.map((groupData) => ({
            ...groupData,
            groupId: group._uid,
          })),
        ];
      }, []),
    [planGroups],
  );

  const tabsData = useMemo(
    () =>
      planGroups.map((group) => ({
        title: group.title,
        key: group._uid,
      })),
    [planGroups],
  );

  const onSelectPlan = useCallback(
    (groupId: string) => {
      setSelectedGroup(groupId);
    },
    [setSelectedGroup],
  );

  return (
    <div
      className={classNames(
        "mt-20 px-5",
        "lg:mt-25 lg:px-16",
        "xl:max-w-container-wide xl:mx-auto xl:mt-40 xl:w-full xl:px-24",
      )}
    >
      <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
        <BlockHeader
          richText={title}
          className={classNames(
            "text-large text-primary-content mb-12",
            "lg:mb-8 lg:text-center",
            "xl:text-xx-large xl:mb-22",
          )}
        />
        <div
          className={classNames(
            "hidden w-fit",
            "lg:mx-auto lg:mb-8 lg:block",
            "xl:mb-18",
          )}
        >
          <Tabs
            tabs={tabsData}
            activeTabKey={selectedGroup}
            onTabChange={onSelectPlan}
          />
        </div>

        <div
          className={classNames(
            "flex flex-col items-center gap-7.5",
            "md:grid md:grid-cols-2",
            "lg:flex lg:flex-row lg:justify-center lg:gap-4",
            "xxl:gap-7.5",
          )}
        >
          {destructuredPlans.map((plan) => (
            <Plan
              key={plan._uid}
              onSelect={onSelectPlan}
              isActive={plan.groupId === selectedGroup}
              {...plan}
            />
          ))}
        </div>
      </Fade>
    </div>
  );
};
